
import { ApplicationRef, inject, Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { first } from "rxjs/operators";
import { BehaviorSubject, Observable } from "rxjs";
import { ServiceWorkerMessage, ServiceWorkerUpdateInfo } from "./notification-interfaces";

@Injectable({ providedIn: "root" })
export class CheckForNotificationSwService {
  private appRef = inject(ApplicationRef);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private isListening = false;
  private stabilityTimeout = 10000; // 10 secondi timeout per app stability
  
  // Service Worker Update Management
  private updateInfoSubject = new BehaviorSubject<ServiceWorkerUpdateInfo>({
    version: "unknown",
    buildTime: "unknown",
    updateAvailable: false,
    isInstalling: false,
    isActivated: false,
    cachesCleared: 0,
    features: {
      pushNotifications: false,
      offlineSupport: false,
      cacheManagement: false
    }
  });

  /**
   * Observable for service worker update information
   */
  public updateInfo$: Observable<ServiceWorkerUpdateInfo> = this.updateInfoSubject.asObservable();

  /**
   * Get current update information
   */
  public get currentUpdateInfo(): ServiceWorkerUpdateInfo {
    return this.updateInfoSubject.value;
  }
    
  constructor() {
    // Allow the app to stabilize first, but don't wait indefinitely
    // to avoid blocking on notification permission dialogs
    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    
    // Promise that resolves when app is stable
    const stablePromise = new Promise<void>((resolve) => {
      appIsStable$.subscribe(() => {
        resolve();
      });
    });

    // Timeout promise that resolves after 10 seconds
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, this.stabilityTimeout); // Timeout configurabile
    });

    // Initialize notification listener when app is stable OR after timeout
    Promise.race([stablePromise, timeoutPromise]).then(() => {
      void this.initializeNotificationListener();
    }).catch(() => {
      // Silent error handling - proceed with initialization anyway
      void this.initializeNotificationListener();
    });
  }

  /**
   * Initialize the notification listener after app is stable
   */
  private async initializeNotificationListener(): Promise<void> {
    try {
      
      if (!("serviceWorker" in navigator)) {
        return;
      }

      // Wait for service worker to be ready with timeout
      const registration = await this.getServiceWorkerReadyWithTimeout();
      
      if (!registration) {
        return;
      }

      // Listen for messages from service worker
      this.setupServiceWorkerMessageListener();

      this.isListening = true;
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Wrapper per navigator.serviceWorker.ready con timeout
   * Evita che il service worker si blocchi sui dialog delle notifiche
   * @param timeoutMs - Timeout in millisecondi (default: 15 secondi)
   * @returns Promise con ServiceWorkerRegistration o null se timeout
   */
  private async getServiceWorkerReadyWithTimeout(timeoutMs: number = 15000): Promise<ServiceWorkerRegistration | null> {
    if (!("serviceWorker" in navigator)) {
      return null;
    }

    try {
      const readyPromise = navigator.serviceWorker.ready;
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          resolve(null);
        }, timeoutMs);
      });

      const result = await Promise.race([readyPromise, timeoutPromise]);
      
      if (result === null) {
        return null;
      }

      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Setup listener for messages from service worker
   */
  private setupServiceWorkerMessageListener(): void {
    navigator.serviceWorker.addEventListener("message", (event: MessageEvent<ServiceWorkerMessage>) => {
      this.ngZone.run(() => {
        this.handleServiceWorkerMessage(event);
      });
    });
  }

  /**
   * Handle messages received from service worker
   */
  private handleServiceWorkerMessage(event: MessageEvent<ServiceWorkerMessage>): void {
    
    if (!event.data) return;

    switch (event.data.type) {
      case "NOTIFICATION_CLICKED":
        this.handleNotificationClicked(event.data);
        break;
      case "NOTIFICATION_CLOSED":
        break;
      case "NAVIGATE_TO":
        this.handleNavigateToRequest(event.data);
        break;
      case "NOTIFICATION_PAYLOAD":
        this.handleNotificationPayload(event.data);
        break;
      case "SW_ACTIVATED":
        this.handleServiceWorkerActivated(event.data);
        break;
      case "SW_UPDATE":
        this.handleServiceWorkerUpdate(event.data);
        break;
      case "SW_ERROR":
        break;
      case "VERSION_INFO":
        this.handleVersionInfo(event.data);
        break;
      case "PONG":
        this.handlePongResponse(event.data);
        break;
      default:
        // Unknown message type
    }
  }

  /**
   * Handle service worker activation events
   */
  private handleServiceWorkerActivated(data: ServiceWorkerMessage): void {
    
    // Ensure features object has all required properties
    const features = {
      pushNotifications: data.features?.pushNotifications ?? false,
      offlineSupport: data.features?.offlineSupport ?? false,
      cacheManagement: data.features?.cacheManagement ?? false
    };
    
    this.updateInfoSubject.next({
      ...this.updateInfoSubject.value,
      version: data.version ?? "unknown",
      buildTime: data.buildTime ?? "unknown",
      isActivated: true,
      isInstalling: false,
      cachesCleared: data.deletedOldCaches ?? 0,
      features
    });
  }

  /**
   * Handle service worker update events
   */
  private handleServiceWorkerUpdate(data: ServiceWorkerMessage): void {
    
    const currentInfo = this.updateInfoSubject.value;
    
    switch (data.updateType) {
      case "INSTALLING":
        this.updateInfoSubject.next({
          ...currentInfo,
          version: data.version ?? currentInfo.version,
          buildTime: data.buildTime ?? currentInfo.buildTime,
          updateAvailable: true,
          isInstalling: true,
          isActivated: false
        });
        break;
        
      case "ACTIVATED": {
        // Ensure features object has all required properties
        const features = data.features ? {
          pushNotifications: data.features.pushNotifications ?? currentInfo.features.pushNotifications,
          offlineSupport: data.features.offlineSupport ?? currentInfo.features.offlineSupport,
          cacheManagement: data.features.cacheManagement ?? currentInfo.features.cacheManagement
        } : currentInfo.features;
        
        this.updateInfoSubject.next({
          ...currentInfo,
          version: data.version ?? currentInfo.version,
          buildTime: data.buildTime ?? currentInfo.buildTime,
          updateAvailable: false,
          isInstalling: false,
          isActivated: true,
          cachesCleared: data.deletedOldCaches ?? currentInfo.cachesCleared,
          features
        });
        break;
      }
        
      case "FORCE_UPDATE":
        if (data.shouldReload) {
          // Small delay to allow service worker to complete
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
        break;
        
      case "CACHES_CLEARED":
        this.updateInfoSubject.next({
          ...currentInfo,
          cachesCleared: data.clearedCount ?? 0
        });
        break;
    }
  }

  /**
   * Handle service worker error events
   */
  private handleServiceWorkerError(): void {
  }

  /**
   * Handle version info response from service worker
   */
  private handleVersionInfo(data: ServiceWorkerMessage): void {
    
    this.updateInfoSubject.next({
      ...this.updateInfoSubject.value,
      version: data.version ?? "unknown",
      buildTime: data.buildTime ?? "unknown"
    });
  }

  /**
   * Handle pong response from service worker
   */
  private handlePongResponse(data: ServiceWorkerMessage): void {
    
    this.updateInfoSubject.next({
      ...this.updateInfoSubject.value,
      version: data.version ?? "unknown",
      buildTime: data.buildTime ?? "unknown"
    });
  }

  /**
   * Get the current listening status
   */
  public get isNotificationListenerActive(): boolean {
    return this.isListening;
  }

  /**
   * Handle notification clicked events from service worker
   */
  private handleNotificationClicked(data: ServiceWorkerMessage): void {
    
    if (data.notification?.data) {
      const { url } = data.notification.data;
      
      // Gestisci navigazione se presente un URL nei dati della notifica
      // (fallback nel caso il service worker non riesca a gestire la navigazione)
      if (url) {
        this.handleNavigateToRequest({ type: "NAVIGATE_TO", url });
      }
    }
  }

  /**
   * Handle navigation requests from service worker
   */
  private handleNavigateToRequest(data: ServiceWorkerMessage): void {
          if (!data.url) {
        return;
      }

    try {
      // Assicurati che l'URL sia relativo per la navigazione Angular
      let targetUrl = data.url;
      
      // Se l'URL è assoluto e corrisponde all'origine corrente, estrarre il path appropriato
      if (targetUrl.startsWith("http")) {
        const url = new URL(targetUrl);
        if (url.origin === window.location.origin) {
          if (data.isHashRouting && url.hash.startsWith("#/")) {
            // Per hash routing, usa solo la parte hash senza il simbolo #
            targetUrl = url.hash.substring(1);
          } else {
            // Per routing normale, usa pathname + search + hash
            targetUrl = url.pathname + url.search + url.hash;
          }
        } else {
          // URL esterno - aprilo in una nuova finestra
          window.open(targetUrl, "_blank");
          return;
        }
      }

      // Naviga usando Angular Router
      void this.router.navigateByUrl(targetUrl);
      
    } catch (error) {
      // Fallback - prova a ricaricare la pagina con la nuova URL
      try {
        const fallbackUrl = data.fullUrl ?? data.url;
        window.location.href = fallbackUrl;
      } catch (fallbackError) {
        // Silent error handling
      }
    }
  }

  /**
   * Handle notification payload messages from service worker
   * This is used when the service worker opens a new window and sends additional data
   */
  private handleNotificationPayload(data: ServiceWorkerMessage): void {
    
    // If we receive a payload with a target URL, navigate to it
    // This is useful when the service worker opens a new window
    const urlToNavigate = data.targetUrl ?? data.url;
    if (urlToNavigate) {
      this.handleNavigateToRequest({ 
        type: "NAVIGATE_TO", 
        url: urlToNavigate,
        isHashRouting: data.isHashRouting,
        fullUrl: data.fullUrl
      });
    }
  }

  /**
   * Enable/disable console logging for notifications
   */
  private enableLogging = true;
  
  public setLogging(enabled: boolean): void {
    this.enableLogging = enabled;
  }

  /**
   * Configura il timeout per l'attesa della stabilità dell'app
   * @param timeoutSeconds - Timeout in secondi per aspettare l'app stability
   */
  public setStabilityTimeout(timeoutSeconds: number): void {
    if (timeoutSeconds < 1) {
      return;
    }

    this.stabilityTimeout = timeoutSeconds * 1000;
  }
}